import type {
  ApiKey,
  AsyncAcceptedResponse,
  AuditEvent,
  BuildPlan,
  BootstrapStatusResponse,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  CreateInvitationRequest,
  CreateInvitationResponse,
  CreateSourceConnectionRequest,
  CreateUploadSessionRequest,
  CreateOwnerRequest,
  CreateOwnerResponse,
  CreateProjectRequest,
  CurrentIdentityResponse,
  FrameworkCandidate,
  GitRepository,
  Invitation,
  LoginChallengeRequest,
  LoginRequest,
  LoginResponse,
  PreflightResponse,
  ProblemDetails,
  ProjectDetailResponse,
  ProjectListResponse,
  Release,
  ReauthenticateRequest,
  ReauthenticateResponse,
  RevokeApiKeyRequest,
  Role,
  Session,
  SourceConnection,
  TeamMember,
  UploadPartRequest,
  UploadSession,
  Job,
} from "@webyonetim/contracts";

export const API_BASE = "/api/v1" as const;

export class ApiError extends Error {
  readonly problem: ProblemDetails;
  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.problem = problem;
  }
}

interface RequestOptions {
  readonly body?: unknown;
  readonly bootstrapToken?: string;
  readonly idempotencyKey?: string;
  readonly method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  readonly rawBody?: BodyInit;
  readonly signal?: AbortSignal;
  readonly suppressUnauthorizedEvent?: boolean;
  readonly withCsrf?: boolean;
}

let csrfToken: string | undefined;

export interface TotpSetupStartResponse {
  readonly requestId: string;
  readonly credentialId: string;
  readonly manualKey: string;
  readonly provisioningUri: string;
  readonly expiresAt: string;
}

export interface EnableTotpRequest {
  readonly credentialId: string;
  readonly code: string;
}
export interface RecoveryCodesResponse {
  readonly requestId: string;
  readonly configuredAt: string;
  readonly recoveryCodes: readonly string[];
}

function isProblemDetails(value: unknown): value is ProblemDetails {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Readonly<Record<string, unknown>>;
  return (
    typeof candidate["status"] === "number" &&
    typeof candidate["code"] === "string" &&
    typeof candidate["requestId"] === "string"
  );
}

async function readPayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) return undefined;
  return response.json() as Promise<unknown>;
}

async function getCsrfToken(signal?: AbortSignal): Promise<string> {
  if (csrfToken !== undefined) return csrfToken;
  const init: RequestInit = { credentials: "same-origin", headers: { Accept: "application/json" } };
  if (signal !== undefined) init.signal = signal;
  const response = await fetch(`${API_BASE}/auth/csrf`, init);
  const payload = await readPayload(response);
  if (
    !response.ok ||
    payload === null ||
    typeof payload !== "object" ||
    typeof (payload as Readonly<Record<string, unknown>>)["csrfToken"] !== "string"
  ) {
    throw new Error("Güvenlik doğrulaması yenilenemedi. Sayfayı yenileyip işlemi tekrar başlatın.");
  }
  const token = (payload as Readonly<Record<string, unknown>>)["csrfToken"];
  if (typeof token !== "string")
    throw new Error("Güvenlik doğrulaması yenilenemedi. Sayfayı yenileyip işlemi tekrar başlatın.");
  csrfToken = token;
  return token;
}

export function clearSensitiveTransportState(): void {
  csrfToken = undefined;
}

function createIdempotencyKey(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (options.body !== undefined && options.rawBody !== undefined) {
    throw new Error("A request cannot contain both JSON and streaming bodies.");
  }
  const method = options.method ?? "GET";
  const headers = new Headers({ Accept: "application/json" });
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.bootstrapToken !== undefined)
    headers.set("X-Bootstrap-Token", options.bootstrapToken);
  if (options.idempotencyKey !== undefined) headers.set("Idempotency-Key", options.idempotencyKey);
  if (options.withCsrf === true) headers.set("X-CSRF-Token", await getCsrfToken(options.signal));
  const init: RequestInit = { credentials: "same-origin", headers, method };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.rawBody !== undefined) init.body = options.rawBody;
  if (options.signal !== undefined) init.signal = options.signal;
  const response = await fetch(`${API_BASE}${path}`, init);
  const payload = await readPayload(response);
  if (!response.ok) {
    if (response.status === 401) {
      clearSensitiveTransportState();
      if (options.suppressUnauthorizedEvent !== true)
        window.dispatchEvent(new CustomEvent("webyonetim:unauthorized"));
    }
    if (response.status === 403) window.dispatchEvent(new CustomEvent("webyonetim:forbidden"));
    if (isProblemDetails(payload)) throw new ApiError(payload);
    throw new Error("Bilgiler yüklenemedi. Bağlantınızı kontrol edip yeniden deneyin.");
  }
  return payload as T;
}

export const setupApi = {
  status: (signal?: AbortSignal) =>
    apiRequest<BootstrapStatusResponse>("/setup/status", signal === undefined ? {} : { signal }),
  preflight: (signal?: AbortSignal) =>
    apiRequest<PreflightResponse>("/setup/preflight", signal === undefined ? {} : { signal }),
  createOwner: (input: CreateOwnerRequest, transientBootstrapToken: string) =>
    apiRequest<CreateOwnerResponse>("/setup/owner", {
      body: input,
      bootstrapToken: transientBootstrapToken,
      method: "POST",
    }),
  startTotp: () =>
    apiRequest<TotpSetupStartResponse>("/setup/totp", { method: "POST", withCsrf: true }),
  confirmTotp: (input: EnableTotpRequest) =>
    apiRequest<RecoveryCodesResponse>("/setup/totp/confirm", {
      body: input,
      method: "POST",
      withCsrf: true,
    }),
  complete: () =>
    apiRequest<{
      readonly requestId: string;
      readonly state: "closed";
      readonly totpEnabled: boolean;
    }>("/setup/complete", { method: "POST", withCsrf: true }),
};

export const authApi = {
  me: (signal?: AbortSignal) =>
    apiRequest<CurrentIdentityResponse>("/auth/me", {
      suppressUnauthorizedEvent: true,
      ...(signal === undefined ? {} : { signal }),
    }),
  login: (input: LoginRequest) =>
    apiRequest<LoginResponse>("/auth/login", { body: input, method: "POST" }),
  mfa: (input: LoginChallengeRequest) =>
    apiRequest<LoginResponse>("/auth/mfa", { body: input, method: "POST" }),
  logout: () =>
    apiRequest<{ readonly loggedOut: true; readonly requestId: string }>("/auth/logout", {
      method: "POST",
      withCsrf: true,
    }),
};

export interface SystemOverview {
  readonly requestId: string;
  readonly checkedAt: string;
  readonly status: "healthy" | "warning" | "error" | "unknown" | "offline" | "checking";
  readonly components: readonly {
    readonly component: "api" | "database" | "queue" | "agent" | "caddy";
    readonly status: "healthy" | "warning" | "error" | "unknown" | "offline" | "checking";
    readonly required: boolean;
    readonly checkedAt: string;
    readonly summary: string;
  }[];
  readonly activeJobs: readonly {
    readonly id: string;
    readonly kind: string;
    readonly status: "queued" | "running" | "reconciling";
    readonly createdAt: string;
  }[];
  readonly recentActivity: readonly {
    readonly occurredAt: string;
    readonly actorLabel: string;
    readonly action: string;
    readonly outcome: "succeeded" | "failed" | "denied";
  }[];
  readonly findings: readonly {
    readonly code: string;
    readonly severity: "warning" | "error";
    readonly summary: string;
    readonly observedAt: string;
  }[];
}

export const systemApi = {
  overview: (signal?: AbortSignal) =>
    apiRequest<SystemOverview>("/system/overview", signal === undefined ? {} : { signal }),
};

export const projectsApi = {
  list: (signal?: AbortSignal, cursor?: string) => {
    const query = new URLSearchParams({ limit: "100" });
    if (cursor !== undefined) query.set("cursor", cursor);
    return apiRequest<ProjectListResponse>(`/projects?${query.toString()}`, {
      ...(signal === undefined ? {} : { signal }),
    });
  },
  detail: (projectId: string, signal?: AbortSignal) =>
    apiRequest<ProjectDetailResponse>(`/projects/${encodeURIComponent(projectId)}`, {
      ...(signal === undefined ? {} : { signal }),
    }),
  create: (input: CreateProjectRequest) =>
    apiRequest<ProjectDetailResponse>("/projects", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
};

export type ManagedDatabaseEngine = "postgresql" | "mysql";

export interface ManagedDatabaseView {
  readonly id: string;
  readonly tenantId: string;
  readonly projectId: string;
  readonly providerIntegrationId: string;
  readonly hostId: string;
  readonly engine: ManagedDatabaseEngine;
  readonly engineVersion: "16.14" | "8.4.11";
  readonly generatedDatabaseName: string;
  readonly generatedLoginName: string;
  readonly lifecycle: "provisioning" | "active" | "rotating" | "tombstoned" | "deleted";
  readonly desiredRevision: number;
  readonly runtimeBinding: {
    readonly environmentVariable: "DATABASE_URL";
    readonly secretBindingId: string;
    readonly secretVersion: number;
    readonly delivery: "worker_only";
  };
}

export interface ManagedBackupView {
  readonly id: string;
  readonly databaseId: string;
  readonly engine: ManagedDatabaseEngine;
  readonly engineVersion: "16.14" | "8.4.11";
  readonly state: "requested" | "verified" | "failed";
  readonly retentionUntil: string;
  readonly verificationState: "pending" | "verified" | "failed";
}

export interface DataServiceActionResponse {
  readonly requestId: string;
  readonly action:
    | {
        readonly state: "accepted";
        readonly jobId: string;
        readonly correlationId: string;
        readonly operation: string;
      }
    | {
        readonly state: "binding_required";
        readonly correlationId: string;
        readonly operation: "provision";
      };
  readonly database?: ManagedDatabaseView;
  readonly runtimeWorkloadBinding?: {
    readonly state: "binding_required" | "bound";
    readonly label: string;
    readonly databaseId: string;
    readonly databaseDesiredRevision: number;
    readonly targetSecretVersion: number;
    readonly workloadId?: string;
    readonly runtimePlanId?: string;
  };
  readonly backup?: ManagedBackupView;
}

export const dataServicesApi = {
  list: (projectId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly ManagedDatabaseView[] }>(
      `/databases?${new URLSearchParams({ projectId }).toString()}`,
      signal === undefined ? {} : { signal },
    ),
  provision: (input: {
    readonly projectId: string;
    readonly providerIntegrationId: string;
    readonly engine: ManagedDatabaseEngine;
    readonly hostId: string;
  }) =>
    apiRequest<DataServiceActionResponse>("/databases", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  bindRuntimeWorkload: (
    databaseId: string,
    input: {
      readonly projectId: string;
      readonly expectedDatabaseRevision: number;
      readonly targetDatabaseRevision: number;
      readonly workloadId: string;
      readonly runtimePlanId: string;
    },
  ) =>
    apiRequest<DataServiceActionResponse>(
      `/databases/${encodeURIComponent(databaseId)}/runtime-workload`,
      {
        body: input,
        idempotencyKey: createIdempotencyKey(),
        method: "PUT",
        withCsrf: true,
      },
    ),
  createBackup: (
    databaseId: string,
    input: {
      readonly projectId: string;
      readonly revision: number;
      readonly confirmationDatabaseName: string;
      readonly reauthenticationId: string;
    },
  ) =>
    apiRequest<DataServiceActionResponse>(`/databases/${encodeURIComponent(databaseId)}/backups`, {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
};

export type ProviderIntegrationKind = "cloudflare" | "postgresql" | "mysql";

export interface ProviderIntegrationView {
  readonly integrationId: string;
  readonly kind: ProviderIntegrationKind;
  readonly name: string;
  readonly hostId?: string;
  readonly state: "pending" | "active" | "degraded" | "revoked";
  readonly version: number;
  readonly metadata: {
    readonly provider: ProviderIntegrationKind;
    readonly supportedVersion?: "16.14" | "8.4.11";
    readonly zoneId?: string;
    readonly zoneName?: string;
    readonly hostId?: string;
    readonly capabilityDigest: `sha256:${string}`;
    readonly observedAt: string;
  };
  readonly lastTestedAt?: string;
}

export const providerIntegrationsApi = {
  list: () =>
    apiRequest<{ readonly requestId: string; readonly items: readonly ProviderIntegrationView[] }>(
      "/provider-integrations",
    ),
  create: (input: {
    readonly kind: ProviderIntegrationKind;
    readonly name: string;
    readonly hostId?: string;
    readonly credential: string;
    readonly requestedCapabilities: readonly string[];
    readonly metadata: ProviderIntegrationView["metadata"];
  }) =>
    apiRequest<{ readonly requestId: string; readonly integration: ProviderIntegrationView }>(
      "/provider-integrations",
      { body: input, idempotencyKey: createIdempotencyKey(), method: "POST", withCsrf: true },
    ),
  revoke: (
    integrationId: string,
    input: { readonly confirmationName: string; readonly reauthenticationId: string },
  ) =>
    apiRequest<{ readonly requestId: string; readonly integration: ProviderIntegrationView }>(
      `/provider-integrations/${encodeURIComponent(integrationId)}`,
      { body: input, idempotencyKey: createIdempotencyKey(), method: "DELETE", withCsrf: true },
    ),
};

export interface TeamMembersResponse {
  readonly items: readonly TeamMember[];
  readonly page: { readonly hasMore: boolean; readonly nextCursor?: string };
}
export interface InvitationsResponse {
  readonly items: readonly Invitation[];
  readonly page: { readonly hasMore: boolean; readonly nextCursor?: string };
}

export const teamApi = {
  members: () => apiRequest<TeamMembersResponse>("/team/members"),
  invitations: () => apiRequest<InvitationsResponse>("/team/invitations"),
  invite: (input: CreateInvitationRequest) =>
    apiRequest<CreateInvitationResponse>("/team/invitations", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  updateRole: (membershipId: string, role: Role, revision: number, reauthenticationId?: string) =>
    apiRequest<{ readonly requestId: string; readonly member: TeamMember }>(
      `/team/members/${encodeURIComponent(membershipId)}`,
      {
        body: {
          role,
          revision,
          ...(reauthenticationId === undefined ? {} : { reauthenticationId }),
        },
        method: "PATCH",
        withCsrf: true,
      },
    ),
  removeMember: (membershipId: string, confirmationEmail: string, reauthenticationId: string) =>
    apiRequest<{ readonly requestId: string; readonly removedAt: string }>(
      `/team/members/${encodeURIComponent(membershipId)}`,
      { body: { confirmationEmail, reauthenticationId }, method: "DELETE", withCsrf: true },
    ),
};

export const apiKeysApi = {
  list: () =>
    apiRequest<{
      readonly items: readonly ApiKey[];
      readonly page: { readonly hasMore: boolean; readonly nextCursor?: string };
    }>("/api-keys"),
  create: (input: CreateApiKeyRequest) =>
    apiRequest<CreateApiKeyResponse>("/api-keys", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  revoke: (apiKeyId: string, input: RevokeApiKeyRequest) =>
    apiRequest<{ readonly requestId: string; readonly revokedAt: string }>(
      `/api-keys/${encodeURIComponent(apiKeyId)}`,
      { body: input, method: "DELETE", withCsrf: true },
    ),
  get: (apiKeyId: string) => apiRequest<ApiKey>(`/api-keys/${encodeURIComponent(apiKeyId)}`),
};

export interface SecurityStatus {
  readonly requestId: string;
  readonly totpEnabled: boolean;
  readonly passwordChangedAt: string;
  readonly reauthenticatedUntil?: string;
}
export const securityApi = {
  status: () => apiRequest<SecurityStatus>("/security"),
  reauthenticate: (input: ReauthenticateRequest) =>
    apiRequest<ReauthenticateResponse>("/security/reauthenticate", {
      body: input,
      method: "POST",
      withCsrf: true,
    }),
  sessions: () =>
    apiRequest<{ readonly requestId: string; readonly sessions: readonly Session[] }>(
      "/security/sessions",
    ),
  revokeSession: (sessionId: string) =>
    apiRequest<{
      readonly requestId: string;
      readonly sessionId: string;
      readonly revokedAt: string;
    }>(`/security/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE", withCsrf: true }),
  revokeOthers: (reauthenticationId: string) =>
    apiRequest<{
      readonly requestId: string;
      readonly revokedCount: number;
      readonly revokedAt: string;
    }>("/security/sessions/revoke-others", {
      body: { reauthenticationId },
      method: "POST",
      withCsrf: true,
    }),
  changePassword: (input: {
    readonly currentPassword: string;
    readonly newPassword: string;
    readonly newPasswordConfirmation: string;
  }) =>
    apiRequest<{
      readonly requestId: string;
      readonly changedAt: string;
      readonly otherSessionsRevoked: boolean;
    }>("/security/password", { body: input, method: "PUT", withCsrf: true }),
  startTotp: () =>
    apiRequest<TotpSetupStartResponse>("/security/totp/setup", { method: "POST", withCsrf: true }),
  enableTotp: (input: EnableTotpRequest) =>
    apiRequest<RecoveryCodesResponse>("/security/totp/enable", {
      body: input,
      method: "POST",
      withCsrf: true,
    }),
  disableTotp: (reauthenticationId: string) =>
    apiRequest<{ readonly requestId: string; readonly disabledAt: string }>("/security/totp", {
      body: { reauthenticationId },
      method: "DELETE",
      withCsrf: true,
    }),
  recoveryCodes: (reauthenticationId: string) =>
    apiRequest<RecoveryCodesResponse>("/security/recovery-codes", {
      body: { reauthenticationId },
      method: "POST",
      withCsrf: true,
    }),
};

export const auditApi = {
  list: (query: URLSearchParams) =>
    apiRequest<{
      readonly items: readonly AuditEvent[];
      readonly page: { readonly hasMore: boolean; readonly nextCursor?: string };
    }>(`/audit-events${query.size === 0 ? "" : `?${query.toString()}`}`),
};

export interface BuildReviewOverride {
  readonly projectRoot: string;
  readonly framework: FrameworkCandidate["framework"];
  readonly packageManager: FrameworkCandidate["packageManager"];
  readonly buildScript?: "build" | "build:production";
  readonly startScript?: "start" | "start:production";
  readonly outputDirectory?: string;
}

export interface BuildReview {
  readonly id: string;
  readonly projectId: string;
  readonly sourceSnapshotId: string;
  readonly configVersion: number;
  readonly source:
    | {
        readonly kind: "git";
        readonly repository: string;
        readonly ref: string;
        readonly commitSha: string;
      }
    | { readonly kind: "zip"; readonly digest: string };
  readonly confidence: "high" | "medium";
  readonly reviewRequired: boolean;
  readonly candidates: readonly FrameworkCandidate[];
  readonly recommendedCandidate: FrameworkCandidate;
  readonly evidence: readonly string[];
  readonly recommendedBuildPlan: BuildPlan;
  readonly recommendedBuildPlanDigest: string;
  readonly choices: readonly BuildReviewChoice[];
  readonly status: "pending" | "approved";
  readonly approval?: {
    readonly override?: BuildReviewOverride;
    readonly buildPlan: BuildPlan;
    readonly buildPlanDigest: string;
    readonly actorType: "user" | "api_key";
    readonly actorId: string;
    readonly approvedAt: string;
  };
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface BuildReviewChoice {
  readonly choiceId: string;
  readonly candidate: FrameworkCandidate;
  readonly override: BuildReviewOverride;
  readonly buildPlan: BuildPlan;
  readonly buildPlanDigest: string;
  readonly recommended: boolean;
}

export const buildReviewsApi = {
  list: (projectId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly BuildReview[] }>(
      `/projects/${encodeURIComponent(projectId)}/build-reviews`,
      signal === undefined ? {} : { signal },
    ),
  approve: (
    projectId: string,
    reviewId: string,
    input: {
      readonly expectedRevision: number;
      readonly acknowledged: true;
      readonly choiceId: string;
    },
  ) =>
    apiRequest<BuildReview>(
      `/projects/${encodeURIComponent(projectId)}/build-reviews/${encodeURIComponent(reviewId)}/approve`,
      {
        body: input,
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
};

export interface EnvironmentReleaseTruth {
  readonly environmentId: string;
  readonly name: string;
  readonly kind: "production" | "staging" | "preview";
  readonly activeReleaseId?: string;
  readonly desiredGeneration: number;
  readonly activationVersion: number;
  readonly revision: number;
  readonly updatedAt: string;
  readonly servingVerified: false;
}

export interface DeploymentView {
  readonly id: string;
  readonly projectId: string;
  readonly environmentId: string;
  readonly releaseId: string;
  readonly jobId: string;
  readonly kind: "deploy" | "rollback" | "preview";
  readonly status:
    | "requested"
    | "staging"
    | "checking"
    | "ready"
    | "activating"
    | "active"
    | "failed"
    | "cancelled"
    | "superseded"
    | "reconciling";
  readonly expectedActiveReleaseId?: string;
  readonly expectedDesiredGeneration: number;
  readonly expectedActivationVersion: number;
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly healthEvidenceDigest?: string;
  readonly failureCode?: string;
  readonly cancelRequestedAt?: string;
  readonly servingVerified: false;
}

export interface PreviewView {
  readonly id: string;
  readonly projectId: string;
  readonly sourceConnectionId: string;
  readonly environmentId: string;
  readonly provider: "github" | "gitlab";
  readonly providerRepositoryId: string;
  readonly pullRequestNumber: number;
  readonly headCommitSha: string;
  readonly desiredGeneration: number;
  readonly desiredState: "present" | "absent";
  readonly status: "requested" | "active" | "closing" | "removed" | "failed" | "reconciling";
  readonly hostnameIntent: string;
  readonly revision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly servingVerified: false;
}

export interface JobLogItem {
  readonly sequence: number;
  readonly stream: "stdout" | "stderr" | "system";
  readonly digest: string;
  readonly sizeBytes: number;
  readonly redacted: true;
  readonly createdAt: string;
  readonly text: string;
}

export const releasesApi = {
  list: (projectId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly Release[] }>(
      `/projects/${encodeURIComponent(projectId)}/releases`,
      signal === undefined ? {} : { signal },
    ),
  detail: (projectId: string, releaseId: string, signal?: AbortSignal) =>
    apiRequest<{
      readonly requestId: string;
      readonly release: Release;
      readonly environments: readonly EnvironmentReleaseTruth[];
    }>(
      `/projects/${encodeURIComponent(projectId)}/releases/${encodeURIComponent(releaseId)}`,
      signal === undefined ? {} : { signal },
    ),
  deployments: (projectId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly DeploymentView[] }>(
      `/projects/${encodeURIComponent(projectId)}/deployments`,
      signal === undefined ? {} : { signal },
    ),
  previews: (projectId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly PreviewView[] }>(
      `/projects/${encodeURIComponent(projectId)}/previews`,
      signal === undefined ? {} : { signal },
    ),
  deploy: (
    projectId: string,
    input: {
      readonly environmentId: string;
      readonly releaseId: string;
      readonly expectedActiveReleaseId?: string;
      readonly expectedDesiredGeneration: number;
      readonly expectedActivationVersion: number;
    },
  ) =>
    apiRequest<AsyncAcceptedResponse>(`/projects/${encodeURIComponent(projectId)}/deployments`, {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  retry: (projectId: string, deploymentId: string, expectedRevision: number) =>
    apiRequest<AsyncAcceptedResponse>(
      `/projects/${encodeURIComponent(projectId)}/deployments/${encodeURIComponent(deploymentId)}/retry`,
      {
        body: { expectedRevision },
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
  rollback: (
    projectId: string,
    environmentId: string,
    input: {
      readonly targetReleaseId: string;
      readonly expectedActiveReleaseId: string;
      readonly expectedDesiredGeneration: number;
      readonly expectedActivationVersion: number;
    },
  ) =>
    apiRequest<AsyncAcceptedResponse>(
      `/projects/${encodeURIComponent(projectId)}/environments/${encodeURIComponent(environmentId)}/rollback`,
      {
        body: input,
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
  cleanupPreview: (
    projectId: string,
    previewId: string,
    input: {
      readonly confirmationId: string;
      readonly expectedRevision: number;
      readonly expectedDesiredGeneration: number;
    },
  ) =>
    apiRequest<AsyncAcceptedResponse>(
      `/projects/${encodeURIComponent(projectId)}/previews/${encodeURIComponent(previewId)}/cleanup`,
      {
        body: input,
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
  logs: (jobId: string, cursor?: string, signal?: AbortSignal) =>
    apiRequest<{
      readonly requestId: string;
      readonly items: readonly JobLogItem[];
      readonly page: { readonly nextCursor?: string; readonly hasMore: boolean };
    }>(
      `/jobs/${encodeURIComponent(jobId)}/logs${cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`}`,
      signal === undefined ? {} : { signal },
    ),
};

export const jobsApi = {
  get: (jobId: string, signal?: AbortSignal) =>
    apiRequest<Job>(`/jobs/${encodeURIComponent(jobId)}`, signal === undefined ? {} : { signal }),
  cancel: (jobId: string) =>
    apiRequest<Job>(`/jobs/${encodeURIComponent(jobId)}/cancel`, {
      body: { confirmationId: jobId },
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
};

export interface OperationsProjection {
  readonly tenantId: string;
  readonly projectId: string;
  readonly hostId: string;
  readonly workloadId: string;
  readonly observedAt: string;
  readonly freshness: "confirmed" | "stale" | "disconnected";
  readonly health: "healthy" | "degraded" | "unhealthy" | "unknown";
  readonly metrics: readonly {
    readonly name:
      "cpu_percent" | "memory_bytes" | "disk_bytes" | "network_rx_bytes" | "network_tx_bytes";
    readonly value: number;
  }[];
  readonly incidents: readonly {
    readonly id: string;
    readonly state: "open" | "resolved";
    readonly reason: string;
    readonly lastSeenAt: string;
  }[];
}

export type OperationsRetentionPreview =
  | {
      readonly state: "disabled";
      readonly reason: "owner_policy_required" | "authoritative_candidates_unavailable";
    }
  | {
      readonly state: "ready";
      readonly candidateIds: readonly string[];
      readonly totalBytes: number;
      readonly digest: string;
    };

export interface OperationsLogPage {
  readonly items: readonly {
    readonly cursor: string;
    readonly stream: "stdout" | "stderr" | "system";
    readonly observedAt: string;
    readonly text: string;
  }[];
  readonly nextCursor?: string;
  readonly state: "confirmed" | "stale" | "disconnected";
}

export type OperationsRetentionPolicy =
  | { readonly state: "unset" }
  | {
      readonly state: "saved";
      readonly revision: number;
      readonly enabled: boolean;
      readonly rules: readonly { readonly target: string; readonly retainDays: number }[];
    };

export const operationsApi = {
  snapshot: (projectId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly OperationsProjection[] }>(
      `/operations?projectId=${encodeURIComponent(projectId)}`,
      signal === undefined ? {} : { signal },
    ),
  runtimeLogs: (
    input: {
      readonly projectId: string;
      readonly jobId: string;
      readonly cursor?: string;
      readonly query?: string;
    },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ projectId: input.projectId, jobId: input.jobId });
    if (input.cursor !== undefined) query.set("cursor", input.cursor);
    if (input.query !== undefined && input.query.trim() !== "")
      query.set("query", input.query.trim());
    return apiRequest<OperationsLogPage>(
      `/operations/runtime-logs?${query.toString()}`,
      signal === undefined ? {} : { signal },
    );
  },
  runtimeLogDownloadPath: (projectId: string, jobId: string) =>
    `${API_BASE}/operations/runtime-logs/download?${new URLSearchParams({ projectId, jobId }).toString()}`,
  restart: (input: {
    readonly projectId: string;
    readonly hostId: string;
    readonly workloadId: string;
    readonly confirmationWorkloadId: string;
    readonly reauthenticationId: string;
  }) =>
    apiRequest<AsyncAcceptedResponse>("/operations/restart", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  retentionPreview: (projectId: string, signal?: AbortSignal) =>
    apiRequest<OperationsRetentionPreview>(
      `/operations/retention-preview?projectId=${encodeURIComponent(projectId)}`,
      signal === undefined ? {} : { signal },
    ),
  retentionPolicy: (projectId: string, signal?: AbortSignal) =>
    apiRequest<OperationsRetentionPolicy>(
      `/operations/retention-policy?projectId=${encodeURIComponent(projectId)}`,
      signal === undefined ? {} : { signal },
    ),
  saveRetentionPolicy: (input: {
    readonly projectId: string;
    readonly confirmationProjectId: string;
    readonly expectedRevision: number;
    readonly enabled: boolean;
    readonly rules: readonly { readonly target: string; readonly retainDays: number }[];
    readonly reauthenticationId: string;
  }) =>
    apiRequest<OperationsRetentionPolicy>("/operations/retention-policy", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "PUT",
      withCsrf: true,
    }),
  applyRetention: (input: {
    readonly projectId: string;
    readonly previewDigest: string;
    readonly confirmationProjectId: string;
    readonly reauthenticationId: string;
  }) =>
    apiRequest<AsyncAcceptedResponse>("/operations/retention-apply", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
};

export interface SafeProjectDraft {
  readonly id: string;
  readonly revision: number;
  readonly status: "draft" | "applied";
  readonly projectId: string;
  readonly sourceConnectionId: string;
  readonly effectiveConfig: {
    readonly framework: "node" | "nextjs" | "static";
    readonly runtime: "docker" | "native";
    readonly limits: {
      readonly cpuMillicores: number;
      readonly memoryMiB: number;
      readonly diskMiB: number;
    };
    readonly network: { readonly egress: "blocked" };
    readonly healthPath: string;
    readonly environmentNames: readonly string[];
    readonly databaseId?: string;
    readonly domainId?: string;
  };
  readonly job?: { readonly id: string; readonly status: "queued"; readonly eventStream: string };
}

export interface ProjectDraftInput {
  readonly revision?: number;
  readonly projectId: string;
  readonly sourceConnectionId: string;
  readonly framework: "node" | "nextjs" | "static";
  readonly runtime?: "docker" | "native";
  readonly trustedNativeAcknowledgement?: true;
  readonly limits?: {
    readonly cpuMillicores: number;
    readonly memoryMiB: number;
    readonly diskMiB: number;
  };
  readonly network?: { readonly egress: "blocked" };
  readonly healthPath?: string;
  readonly environment?: readonly { readonly name: string; readonly value: string }[];
  readonly databaseId?: string;
  readonly domainId?: string;
}

export const projectDraftsApi = {
  create: (input: ProjectDraftInput) =>
    apiRequest<SafeProjectDraft>("/project-drafts", {
      body: input,
      method: "POST",
      withCsrf: true,
    }),
  get: (draftId: string, signal?: AbortSignal) =>
    apiRequest<SafeProjectDraft>(
      `/project-drafts/${encodeURIComponent(draftId)}`,
      signal === undefined ? {} : { signal },
    ),
  update: (draftId: string, input: ProjectDraftInput) =>
    apiRequest<SafeProjectDraft>(`/project-drafts/${encodeURIComponent(draftId)}`, {
      body: input,
      method: "PUT",
      withCsrf: true,
    }),
  apply: (draftId: string, revision: number) =>
    apiRequest<SafeProjectDraft>(`/project-drafts/${encodeURIComponent(draftId)}/apply`, {
      body: { revision },
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
};

export interface SourceConnectionMutationResponse {
  readonly requestId: string;
  readonly connection: SourceConnection;
  readonly webhookSetup?: { readonly endpoint: string; readonly connectionId: string };
}

export interface GithubInstallationStartResponse {
  readonly requestId: string;
  readonly state: string;
  readonly installationUrl: string;
  readonly expiresAt: string;
}

export interface GithubRepositorySelectionResponse {
  readonly requestId: string;
  readonly selectionRequired: true;
  readonly selectionToken: string;
  readonly expiresAt: string;
  readonly repositories: readonly GitRepository[];
}

export type GithubInstallationCallbackResponse =
  SourceConnectionMutationResponse | GithubRepositorySelectionResponse;

export function isGithubRepositorySelection(
  response: GithubInstallationCallbackResponse,
): response is GithubRepositorySelectionResponse {
  return "selectionRequired" in response && response.selectionRequired;
}

export interface ProviderRef {
  readonly name: string;
  readonly commitSha: string;
  readonly protected: boolean;
}

export const sourcesApi = {
  list: (projectId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly SourceConnection[] }>(
      `/projects/${encodeURIComponent(projectId)}/sources`,
      signal === undefined ? {} : { signal },
    ),
  create: (
    projectId: string,
    input: Exclude<CreateSourceConnectionRequest, { readonly provider: "github" }>,
  ) =>
    apiRequest<SourceConnectionMutationResponse>(
      `/projects/${encodeURIComponent(projectId)}/sources`,
      {
        body: input,
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
  startGithub: (projectId: string) =>
    apiRequest<GithubInstallationStartResponse>(
      `/projects/${encodeURIComponent(projectId)}/sources/github/installations`,
      {
        body: {},
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
  completeGithub: (input: {
    readonly state: string;
    readonly code: string;
    readonly installationId: string;
  }) =>
    apiRequest<GithubInstallationCallbackResponse>("/sources/github/callback", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  selectGithub: (input: {
    readonly selectionToken: string;
    readonly providerRepositoryId: string;
  }) =>
    apiRequest<SourceConnectionMutationResponse>("/sources/github/selections", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  test: (projectId: string, sourceConnectionId: string) =>
    apiRequest<{
      readonly requestId: string;
      readonly status: "reachable" | "validated";
      readonly checkedAt: string;
      readonly repositoryCount?: number;
    }>(
      `/projects/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(sourceConnectionId)}/test`,
      {
        body: {},
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
  repositories: (projectId: string, sourceConnectionId: string, signal?: AbortSignal) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly GitRepository[] }>(
      `/projects/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(sourceConnectionId)}/repositories`,
      signal === undefined ? {} : { signal },
    ),
  refs: (
    projectId: string,
    sourceConnectionId: string,
    providerRepositoryId: string,
    signal?: AbortSignal,
  ) =>
    apiRequest<{ readonly requestId: string; readonly items: readonly ProviderRef[] }>(
      `/projects/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(sourceConnectionId)}/repositories/${encodeURIComponent(providerRepositoryId)}/refs`,
      signal === undefined ? {} : { signal },
    ),
  snapshot: (
    projectId: string,
    sourceConnectionId: string,
    input: {
      readonly ref: string;
      readonly commitSha: string;
      readonly expectedProjectRevision: number;
    },
  ) =>
    apiRequest<AsyncAcceptedResponse>(
      `/projects/${encodeURIComponent(projectId)}/sources/${encodeURIComponent(sourceConnectionId)}/snapshots`,
      {
        body: input,
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        withCsrf: true,
      },
    ),
};

export const uploadsApi = {
  create: (input: CreateUploadSessionRequest) =>
    apiRequest<UploadSession>("/uploads", {
      body: input,
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  get: (uploadSessionId: string, signal?: AbortSignal) =>
    apiRequest<UploadSession>(`/uploads/${encodeURIComponent(uploadSessionId)}`, {
      ...(signal === undefined ? {} : { signal }),
    }),
  append: (
    uploadSessionId: string,
    metadata: UploadPartRequest,
    bytes: Blob,
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({
      partNumber: String(metadata.partNumber),
      offset: String(metadata.offset),
      contentLength: String(metadata.contentLength),
      digest: metadata.digest,
      expectedRevision: String(metadata.expectedRevision),
    });
    const form = new FormData();
    form.append("file", bytes, "part.bin");
    return apiRequest<UploadSession>(
      `/uploads/${encodeURIComponent(uploadSessionId)}/parts?${query.toString()}`,
      {
        idempotencyKey: createIdempotencyKey(),
        method: "POST",
        rawBody: form,
        ...(signal === undefined ? {} : { signal }),
        withCsrf: true,
      },
    );
  },
  complete: (uploadSessionId: string, digest: string, expectedRevision: number) =>
    apiRequest<AsyncAcceptedResponse>(`/uploads/${encodeURIComponent(uploadSessionId)}/complete`, {
      body: { digest, expectedRevision },
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
  cancel: (uploadSessionId: string, expectedRevision: number) =>
    apiRequest<UploadSession>(`/uploads/${encodeURIComponent(uploadSessionId)}/cancel`, {
      body: { expectedRevision },
      idempotencyKey: createIdempotencyKey(),
      method: "POST",
      withCsrf: true,
    }),
};
